// Self-contained "Scheduled Calls" section for the lead detail page.
// Fetches the lead's bookings (exact lead_id filter), splits upcoming vs past,
// and lets the user book a new call prefilled with this lead's contact info.
// Designed to drop into LeadDetails with a single import + one line of JSX.
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaPhoneAlt, FaPlus, FaChevronRight } from 'react-icons/fa';
import { DetailSection } from '../../../components/styled/DetailComponents';
import ModalNotification from '../../../components/shared/ModalNotification';
import StatusBadge from './StatusBadge';
import BookingModal from './BookingModal';
import { listCalls } from '../callScheduleService';
import { formatEastern } from '../utils/scheduleUtils';

const BookBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px; align-self: flex-start;
  padding: 7px 12px; border: 1px solid ${p => p.theme.colors.primary}; border-radius: 8px;
  background: transparent; color: ${p => p.theme.colors.primary}; font-weight: 600;
  font-size: 0.82rem; cursor: pointer;
  &:hover { background: ${p => p.theme.colors.primary}18; }
`;

const SubHead = styled.div`
  font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px;
  color: ${p => p.theme.colors.textSecondary}; margin: 6px 0 2px;
`;

const Row = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  background: ${p => p.theme.colors.background}; border-radius: 8px;
  padding: 10px 12px; cursor: pointer; transition: transform 0.15s ease;
  &:hover { transform: translateX(3px); }
  .when { color: ${p => p.theme.colors.textPrimary}; font-size: 0.88rem; }
  .right { display: flex; align-items: center; gap: 10px; color: ${p => p.theme.colors.textSecondary}; }
`;

const EmptyText = styled.div`
  color: ${p => p.theme.colors.textSecondary}; font-size: 0.85rem; padding: 4px 0;
`;

const LeadScheduledCalls = ({ lead }) => {
  const navigate = useNavigate();
  const leadId = lead?.id;
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [bookOpen, setBookOpen] = useState(false);
  const [feedback, setFeedback] = useState({ show: false, message: '', success: true });

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const res = await listCalls({ lead_id: leadId, sort: 'slot_start', order: 'asc', limit: 50 });
      const now = Date.now();
      const calls = res.calls || [];
      setUpcoming(calls.filter(c => new Date(c.slot_start).getTime() >= now));
      setPast(calls.filter(c => new Date(c.slot_start).getTime() < now).reverse());
    } catch (err) {
      // Non-fatal: the rest of the lead page should still render.
      console.error('Error loading scheduled calls:', err);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const renderRow = (call) => (
    <Row key={call.id} onClick={() => navigate(`/bookings/${call.id}`)}>
      <span className="when">{formatEastern(call.slot_start)}</span>
      <span className="right"><StatusBadge status={call.status} /><FaChevronRight size={11} /></span>
    </Row>
  );

  return (
    <DetailSection>
      <h3><FaPhoneAlt /> Scheduled Calls</h3>

      <SubHead>Upcoming</SubHead>
      {upcoming.length === 0 ? <EmptyText>No upcoming calls.</EmptyText> : upcoming.map(renderRow)}

      {past.length > 0 && (
        <>
          <SubHead>Past</SubHead>
          {past.map(renderRow)}
        </>
      )}

      <BookBtn onClick={() => setBookOpen(true)}><FaPlus size={11} /> Book a Call</BookBtn>

      <BookingModal
        isOpen={bookOpen}
        mode="create"
        prefill={{ lead_id: leadId, name: lead?.name, email: lead?.email, phone: lead?.phone }}
        onClose={() => setBookOpen(false)}
        onSaved={(msg) => { setFeedback({ show: true, message: msg, success: true }); load(); }}
      />

      <ModalNotification
        isOpen={feedback.show}
        message={feedback.message}
        success={feedback.success}
        onComplete={() => setFeedback(prev => ({ ...prev, show: false }))}
      />
    </DetailSection>
  );
};

export default LeadScheduledCalls;
